import type { ApprovalActionContext, ApprovalSummary, RunEvent } from "@carbon-operations/contracts";
import { approvalRequestFor, eventsMatchRunBoundary } from "./contract-builders.js";
import { approvalActionContextSha256 } from "./approval-context-integrity.js";

export interface ApprovalQueueRow {
  approval_id: string;
  run_id: string;
  capability: string;
  expires_at: string;
  requester: string;
  action_context?: ApprovalActionContext;
  /** False when the run's ordered approval.requested event does not match this approval; the row still links to the run. */
  evidence: boolean;
}

/**
 * Pending approvals for one workspace, soonest expiry first. Action facts and the requesting operator come from
 * the run's own approval.requested event (the same parser the run-detail decision uses), never from the list row.
 */
export function approvalQueueRows(
  approvals: ApprovalSummary[],
  eventsByRun: ReadonlyMap<string, RunEvent[] | null>,
  boundary: { tenantId: string; workspaceId: string },
): ApprovalQueueRow[] {
  return approvals
    .filter((approval) => approval.state === "pending" && approval.workspace_id === boundary.workspaceId)
    .map((approval) => {
      const events = eventsByRun.get(approval.run_id) ?? null;
      const request = events !== null &&
          eventsMatchRunBoundary(events, { ...boundary, runId: approval.run_id })
        ? approvalRequestFor(events, approval.approval_id)
        : null;
      const evidence = request !== null && request.expected_action_sha256 === approval.expected_action_sha256 &&
        Date.parse(request.expires_at) === Date.parse(approval.expires_at) &&
        request.action_context.tenant_id === boundary.tenantId &&
        request.action_context.workspace_id === approval.workspace_id &&
        request.action_context.capability_id === approval.capability &&
        request.action_context.requester.actor_id === approval.requested_by;
      return {
        approval_id: approval.approval_id,
        run_id: approval.run_id,
        capability: approval.capability,
        // The expiry shown is the event's; without matching event evidence it is not shown as a countdown.
        expires_at: evidence ? request.expires_at : approval.expires_at,
        requester: evidence ? request.action_context.requester.actor_id : approval.requested_by,
        ...(evidence ? { action_context: request.action_context } : {}),
        evidence,
      };
    })
    .sort((a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at) || a.approval_id.localeCompare(b.approval_id));
}

/** Queue rows shown as verified must also reproduce the list's server-owned action digest. */
export async function verifiedApprovalQueueRows(
  approvals: ApprovalSummary[],
  eventsByRun: ReadonlyMap<string, RunEvent[] | null>,
  boundary: { tenantId: string; workspaceId: string },
): Promise<ApprovalQueueRow[]> {
  const expectedById = new Map(approvals.map((approval) => [approval.approval_id, approval.expected_action_sha256]));
  return Promise.all(approvalQueueRows(approvals, eventsByRun, boundary).map(async (row) => {
    if (!row.evidence || row.action_context === undefined) return row;
    try {
      if (await approvalActionContextSha256(row.action_context) === expectedById.get(row.approval_id)) return row;
    } catch {
      // Invalid or unavailable browser hashing is unverified, never approval evidence.
    }
    return {
      approval_id: row.approval_id,
      run_id: row.run_id,
      capability: row.capability,
      expires_at: row.expires_at,
      requester: row.requester,
      evidence: false,
    };
  }));
}

/**
 * What the Approvals page may do for a workspace-local membership. Deciding needs an owner/admin/approver role
 * and approvals:decide; reviewing also reads the run and its events, which needs runs:read.
 */
export function approvalAccess(membership: { roles: readonly string[]; scopes: readonly string[] }): "review" | "decide-only" | "none" {
  const mayDecide = membership.roles.some((role) => role === "owner" || role === "admin" || role === "approver") &&
    membership.scopes.includes("approvals:decide");
  if (!mayDecide) return "none";
  return membership.scopes.includes("runs:read") ? "review" : "decide-only";
}

/** "14:05 left", "1:02:09 left", or "Expired". */
export function expiryCountdown(expiresAt: string, nowMs: number): string {
  const remaining = Math.floor((Date.parse(expiresAt) - nowMs) / 1_000);
  if (!Number.isFinite(remaining) || remaining <= 0) return "Expired";
  const hours = Math.floor(remaining / 3_600);
  const minutes = Math.floor((remaining % 3_600) / 60);
  const seconds = String(remaining % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds} left` : `${minutes}:${seconds} left`;
}
